import { LgpdContent, LgpdContentSignature } from '@app/services/selective-processes/component/lgpd/lgpd-consent-checkbox-options';
import { validValue } from '@shared/utils/util';
import { Formio } from 'angular-formio';

const Base = (Formio as any).Components.components.component;
const Components = (Formio as any).Components;

export class Lgpd extends Base {

  // Valores do formbuild do componente
  private component;
  // opções do form render
  private options;
  // objeto com o valor do componente
  private data;
  // verifica se o componente está no editor
  private editor;

  private consentId: number;
  private signatures: Array<LgpdContentSignature>;
  private inputs = [];
  private checks: any;
  private consentProcessCheck: any;


  constructor(component, options, data) {
    super(component, options, data);
    this.component = component;
    this.options = options;
    this.data = data;
    this.editor = options.editor;
  }

  public static schema() {
    return Base.schema({
      type: 'lgpd'
    });
  }

  /**
   * Render returns an html string of the fully rendered component.
   *
   * @param children - If this class is extendended, the sub string is passed as children.
   * @returns string
   */
  public render(): string {
    let content = '';
    if (this.component.lgpd_content) {
      content += this.buildContent(this.component.lgpd_content);
    }
    return super.render(`<div class="lgpd-layout-column">${content}</div>`);
  }

  private buildContent(lgpdContent: LgpdContent): string {
    this.consentId = parseInt(lgpdContent.consent_id);
    this.signatures = lgpdContent.signatures;

    let initContent = '';

    if (validValue(lgpdContent.text_consent_communication) && this.consentId !== 3) {
      initContent += this.buildRow({
        content: lgpdContent.text_consent_communication
      });
    }

    if (validValue(lgpdContent.signatures)) {
      if (this.consentId !== 3) {
        initContent += this.buildCheckbox(lgpdContent.signatures);
      } else if (this.consentId === 3) {
        initContent += `<div class="lgpd-consent-hidden-input">${this.buildCheckbox(lgpdContent.signatures)}</div>`
      }
    }

    if (validValue(lgpdContent.text_privacy)) {
      const order = this.consentId === 2 ?  'order-6' : '';
      initContent += this.buildRow({
        content: lgpdContent.text_privacy,
        order
      });
    }

    if (validValue(lgpdContent.text_consent_process) && this.consentId !== 3) {
      initContent += this.buildRow({
        content: lgpdContent.text_consent_process
      });
    }

    if (validValue(lgpdContent.label_consent_process) && this.consentId === 2) {
      const checkbox = this.createCheckbox('consent-process');
      initContent += this.buildRow({
        content: `${checkbox} <label for="signature-consent-process-${this.component.id}" class="lgpd-label-checkbox">${lgpdContent.label_consent_process}</label> *`,
        align: 'center'
      });
    }

    if (validValue(lgpdContent.footer_consent_process) && this.consentId === 2) {
      initContent += this.buildRow({
        content: lgpdContent.footer_consent_process
      });
    }

    return initContent;
  }

  private buildRow(item: { content: string; align?: string; order?: string; }): string {
    return `
      <div class="lgpd-layout-row ${item.order ?? ''}">
        <div class="lgpd-layout-flex ${item.align ?? ''}">
          ${item.content}
        </div>
      </div>
    `;
  }

  private buildCheckbox(signatures: Array<LgpdContentSignature>) {
    let init = '';
    signatures.map((signature, index) => {
      const checkbox = this.createCheckbox(index);
      init += this.buildRow({
        content: `${checkbox} <label for="signature-${index ?? ''}-${this.component.id}" class="lgpd-label-checkbox">
        ${signature.label ?? '...'}</label>${signature.required ? ' *' : ''}`,
        align: 'center'
      });
    });
    return init;
  }

  private createCheckbox(signatureIndex?) {
    return this.renderTemplate('input', {
      input: {
        type: 'input',
        ref: `signature-${signatureIndex ?? ''}-${this.component.id}`,
        class: 'form-check-input-select',
        attr: {
          id: `signature-${signatureIndex ?? ''}-${this.component.id}`,
          type: 'checkbox'
        }
      }
    });
  }

  /**
   * After the html string has been mounted into the dom, the dom element is returned here. Use refs to find specific
   * elements to attach functionality to.
   *
   * @param element HTMLElement
   * @returns Promise
   */
  attach(element: HTMLElement): Promise<any> {
    const lgpdContent = Array.isArray(this.signatures) ? this.signatures : false;
    this.loadLgpdElementRefs(element);

    if (lgpdContent) {
      this.checks = [];
      lgpdContent.map((signature, index) => {
        this.checks[index] = {
          field: signature.field_id_to_save_signature,
          input: Array.prototype.slice.call(this.refs[`signature-${index ?? ''}-${this.component.id}`], 0)[0]
        };
        // Attach click events to each input in the row
        this.addEventListener(this.checks[index].input, 'click', () => {this.updateValue()});
      });

      this.consentProcessCheck = {
        field: this.component.lgpd_content.field_id_to_save_consent_process,
        input: this.refs[`signature-consent-process-${this.component.id}`] ? this.refs[`signature-consent-process-${this.component.id}`][0] ?? false : false
      }
      if (this.consentProcessCheck) {
        this.addEventListener(this.consentProcessCheck.input, 'click', () => this.updateValue());
      }
    }
    return super.attach(element);
  }


  /**
   * carrega as referencias especificas do elemento Lgpd no array global de referencias do DOM
   * @param element HTMLElement
   */
  loadLgpdElementRefs(element: HTMLElement): void {
    const lgpdContent = Array.isArray(this.signatures) ? this.signatures : false;
    const refs = [];

    if (lgpdContent) {
      lgpdContent.map((signature, index) => {
        refs[`signature-${index ?? ''}-${this.component.id}`] = 'multiple';
        refs[`signature-consent-process-${this.component.id}`] = 'multiple';
      });
    }
    this.loadRefs(element, refs);
  }


  /**
   * Get the value of the component from the dom elements.
   * @returns Array<string | boolean> key do array é o id do campo e o valor é o id da assinatura
   */
  getValue(): Array<string | boolean> {
    const value = [];
    const lgpdContent = Array.isArray(this.signatures) ? this.signatures : false;
    if (lgpdContent) {
      for (const rowIndex of Object.keys(this.checks)) {
        const row = this.checks[rowIndex];
        value[rowIndex] = [];
        for (const colIndex of Object.keys(row)) {
          const col = row[colIndex];
          value[rowIndex] = {
            field: lgpdContent[rowIndex].field_id_to_save_signature,
            status: col.checked ? '1' : '2'
          };
        }
      }
      if (this.component.lgpd_content.consent_id === 2) {
        value.push(
          {
            field: this.component.lgpd_content.field_id_to_save_consent_process,
            status: this.consentProcessCheck.input.checked ? '1' : '2'
          }
        );
      }
    }
    return value;
  }


  /**
   * Set the value of the component into the dom elements.
   * então o valor vai ficar em um item do this.component.fileList em item.url
   * @param value
   * @returns boolean
   */
  public setValue(values): void | boolean {
    // const calue = input.filter(val => val.field === element.field_id_to_save_signature)[0];
    if (values && Array.isArray(values)) {
      values.map(value => {
        const status = value.status == "2" ? false : true;
        const signatureItem = this.checks.filter(check => check.field == value.field)[0];
        if (signatureItem) {
          signatureItem.input.value = status;
          signatureItem.input.checked = status;
        }
        if (this.component.lgpd_content.field_id_to_save_consent_process && (this.consentProcessCheck.field == value.field)) {
          this.consentProcessCheck.input.value = status;
          this.consentProcessCheck.input.checked = status;
        }
      });
      this.updateValue();
    }
  }

}


export function registryLgpdComponent(): void {
  Components.addComponent('lgpd', Lgpd);
}
